import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { Navigate, NavLink, Outlet, useNavigate, useParams } from "react-router";
import { appSession } from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import { appStyles } from "../../../AppStyles.ts";

// TODO: Replace with real counts loaded from API
function createSampleCounts() {
    return {
        summary: 0,
        details: 0,
        media: 0,
        timeline: 0,
        web: 0,
        resolvers: 0,
        assoc: 0,
        resources: 0,
        insight: 0,
        factors: 0,
        enrichment: 0,
        research: 0,
        provenance: 0,
        transactions: 0,
    };
}

function NavCount({ count }: { count: number }) {
    return (
        <Typography component="span" sx={appStyles.leftNavItemCount}>
            {count}
        </Typography>
    );
}

function UXIntelViewer() {
    const { networkUID, typeKey, defKey, matchHash } = useParams();
    const navigate = useNavigate();
    const counts = createSampleCounts();

    // TODO: Implement graph navigation
    const onGraph = () => {
        navigate(`/networks/${networkUID}/graph`);
    };

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {appSession.activeSession() === false ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title={`Intelligence: ${typeKey}-${defKey}-${matchHash}`}
                        breadcrumbs={[
                            { display: "Networks", linkTo: `/networks/`, activeView: false },
                            { display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false },
                            { display: "Intelligence", linkTo: `/networks/${networkUID}`, activeView: false },
                            { display: String(typeKey).charAt(0).toUpperCase() + String(typeKey).slice(1), linkTo: `/networks/${networkUID}`, activeView: false },
                            { display: String(defKey).charAt(0).toUpperCase() + String(defKey).slice(1), linkTo: `/networks/${networkUID}/intel/${typeKey}/${defKey}`, activeView: false },
                            { display: String(matchHash), linkTo: "", activeView: true },
                        ]}
                    />

                    <Stack direction="row" spacing={1} alignItems="stretch" width="100%" height="100%">
                        <Stack
                            sx={appStyles.bannerBox}
                            direction="column"
                            height="100%"
                        >
                            <Stack sx={appStyles.leftNavInner}>
                                <img
                                    src="/images/default_network_image.png"
                                    height={128}
                                    style={{ margin: "0 auto", paddingTop: 15, display: "block" }}
                                    alt=""
                                />

                                <Stack direction="row" spacing={1} justifyContent="center" sx={{paddingTop: "12px"}}>
                                    <Tooltip title="Go to Graph" placement="bottom">
                                        <Box
                                            component="button"
                                            sx={{...appStyles.leftNavGraphButton, padding: "6px 10px"}}
                                            onClick={onGraph}
                                        >
                                            <img
                                                src="/images/graph_white.svg"
                                                alt="Graph"
                                                width={20}
                                                height={20}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Stack>

                                <List dense disablePadding sx={appStyles.leftNavList} subheader={<li />}>
                                    <li>
                                        <ul style={{ padding: 0, margin: 0 }}>
                                            <ListSubheader sx={appStyles.leftNavSectionHeader}>
                                                Profile Details
                                            </ListSubheader>

                                            <ListItemButton
                                                component={NavLink}
                                                to={`summary`}
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Summary Report"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/summary_report.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Summary Report" />
                                                <NavCount count={counts.summary} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="details"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Details"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/details.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Details" />
                                                <NavCount count={counts.details} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="images"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Media"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/images.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Media" />
                                                <NavCount count={counts.media} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="timeline"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Timeline"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/timeline_white.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Timeline" />
                                                <NavCount count={counts.timeline} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="web"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Web References"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/web_references.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Web References" />
                                                <NavCount count={counts.web} />
                                            </ListItemButton>
                                        </ul>
                                    </li>
                                    <li>
                                        <ul style={{ padding: 0, margin: 0 }}>
                                            <ListSubheader sx={appStyles.leftNavSectionHeader}>
                                                Profile Resolution
                                            </ListSubheader>

                                            <ListItemButton
                                                component={NavLink}
                                                to="resolvers"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Resolvers"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/resolvers.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Resolvers" />
                                                <NavCount count={counts.resolvers} />
                                            </ListItemButton>
                                        </ul>
                                    </li>
                                    <li>
                                        <ul style={{ padding: 0, margin: 0 }}>
                                            <ListSubheader sx={appStyles.leftNavSectionHeader}>
                                                Related
                                            </ListSubheader>
                                            <ListItemButton
                                                component={NavLink}
                                                to="assoc"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Intelligence Associations"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/intelligence_associations.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Intelligence Associations" />
                                                <NavCount count={counts.assoc} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="resources"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Resources"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/resources.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Resources" />
                                                <NavCount count={counts.resources} />
                                            </ListItemButton>
                                        </ul>
                                    </li>
                                    <li>
                                        <ul style={{ padding: 0, margin: 0 }}>
                                            <ListSubheader sx={appStyles.leftNavSectionHeader}>
                                                Analysis
                                            </ListSubheader>
                                            <ListItemButton
                                                component={NavLink}
                                                to="insight"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Analyst Insight"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/analyst_insight.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Analyst Insight" />
                                                <NavCount count={counts.insight} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="factors"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Factors"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/factors.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Factors" />
                                                <NavCount count={counts.factors} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="enrichment"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Enrichment"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/enrichment.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Enrichment" />
                                                <NavCount count={counts.enrichment} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="research"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Research"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/research.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Research" />
                                                <NavCount count={counts.research} />
                                            </ListItemButton>
                                        </ul>
                                    </li>
                                    <li>
                                        <ul style={{ padding: 0, margin: 0 }}>
                                            <ListSubheader sx={appStyles.leftNavSectionHeader}>
                                                Links
                                            </ListSubheader>

                                            <ListItemButton
                                                component={NavLink}
                                                to="provenance"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Provenance Links"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/provenance.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Provenance Links" />
                                                <NavCount count={counts.provenance} />
                                            </ListItemButton>
                                            <ListItemButton
                                                component={NavLink}
                                                to="transactions"
                                                sx={[appStyles.leftNavItem, appStyles.leftNavItemTeal]}
                                                aria-label="Transactions"
                                            >
                                                <ListItemIcon sx={appStyles.leftNavItemIcon}>
                                                    <img
                                                        src="/images/transactions.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        width={20}
                                                        height={20}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary="Transactions" />
                                                <NavCount count={counts.transactions} />
                                            </ListItemButton>
                                        </ul>
                                    </li>

                                </List>
                            </Stack>
                        </Stack>
                        <Stack
                            direction="column"
                            spacing={2}
                            width="100%"
                            height="100%"
                            alignItems="center"
                        >
                            <Stack
                                sx={{ ...appStyles.defaultPanelContainer, position: "relative" }}
                                direction="row"
                                padding={1}
                                alignItems="center"
                                width="100%"
                            >
                                <Stack direction="column" alignItems="center" width="100%" spacing={0.5} padding={1}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <img
                                            src={`/images/intel/${defKey}.svg`}
                                            alt=""
                                            aria-hidden="true"
                                            height={32}
                                        />
                                        <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#38ff82" }}>
                                            {String(matchHash)}
                                        </Typography>
                                    </Stack>
                                    <Typography sx={{ ...appStyles.mainContentTitle, fontSize: 12, opacity: 0.7, textAlign: "center" }}>
                                        Intelligence Profile
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ position: "absolute", right: 16 }}>
                                    <img src="/images/intel/user_profile.svg" height={22} alt="" aria-hidden="true" />
                                    <Typography sx={{ ...appStyles.mainIntelTitle, fontSize: 11, fontWeight: 500, opacity: 0.6 }}>
                                        {String(typeKey)}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Outlet />
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXIntelViewer;
